import { useImportResult } from "@synthesis/shared-services/external/application/hooks";
import { useImportRequest } from "@synthesis/shared-services/internal/application/hooks";
import { ApiError } from "@synthesis/shared-services";
import { setSnack } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";

import { useClientType } from "../../shared/hooks/use-client-type";
import { invalidateExternalApplicationQueries, invalidateInternalApplicationQueries } from "../../shared/utils/invalidate-application-queries";

type UseImportApplicationMutationOptions = {
  requestId?: string;
  onImported?: () => void;
};

const handleImportError = (error: unknown) => {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return;
  }

  if (error.response?.errorCode === "DUPLICATE_IMPORT") {
    setSnack({
      title: "Ошибка",
      description: "Заявка с таким номером уже существует",
      messageType: "error",
    });
    return;
  }

  if (error.response?.errorCode === "INVALID_ARCHIVE") {
    setSnack({
      title: "Ошибка",
      description: "Невалидный архив. Проверьте формат файла и попробуйте снова",
      messageType: "error",
    });
    return;
  }

  if (error.response?.description) {
    setSnack({
      title: "Ошибка",
      description: error.response.description,
      messageType: "error",
    });
  }
};

export const useImportApplicationMutation = ({ requestId, onImported }: UseImportApplicationMutationOptions = {}) => {
  const clientType = useClientType();
  const queryClient = useQueryClient();
  const isExternal = clientType === "external";

  const mutationOptions = {
    gcTime: Infinity,
    onSuccess: async () => {
      if (clientType === "internal") {
        await invalidateInternalApplicationQueries(queryClient, requestId);
      } else {
        await invalidateExternalApplicationQueries(queryClient, requestId);
      }

      onImported?.();
    },
    onError: handleImportError,
  } as const;

  const internalMutation = useImportRequest({ mutation: mutationOptions });
  const externalMutation = useImportResult({ mutation: mutationOptions });

  return isExternal ? externalMutation : internalMutation;
};
